import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
  ApiProductsList,
  ApiProduct,
  ApiSendToAFriendProductRequestBody,
  ApiEmptyResponse,
  ApiProductMocacos,
  ApiProductsV3,
  ApiProductsStockList,
  ApiRelatedProductDetailList,
  ApiProductKeyType
} from 'idt-ecommerce-api/_api/api-catalog/api-products/api-products.models';
import { RequestParams } from 'idt-ecommerce-api/_api/api-util/api-request.models';
import { ApiRequestService } from 'idt-ecommerce-api/_api/api-util/api-request.service';
import { EccomerceApiRoutes } from 'idt-ecommerce-api/shared/url-resolver//resources/eccomerce-api.routes';
import { EcommerceUrlResolverService } from 'idt-ecommerce-api/shared/url-resolver/ecommerce-url-resolver.service';


export interface ApiProductsServiceInterface {
  categoryProducts(storeId: string, catalogId: string, categoryId: string, languageId: string): Observable<ApiProductsList>;
  categoryProductsV3(storeId: string, catalogId: string, categoryId: string, languageId: string):
    Observable<ApiProductsV3>;
  searchProducts(storeId: string, catalogId: string, searchTerm: string, maxResults: string, languageId: string): Observable<ApiProductsList>;
  productDetail(storeId: string, catalogId: string, categoryId: string, productId: string, languageId: string): Observable<ApiProduct>;
  productsDetailArray(storeId: string, catalogId: string, productIds: string,
                      languageId: string): Observable<ApiProductsList>;
  relatedProductsDetail(storeId: string, catalogId: string, categoryId: string, productId: string,
                        languageId: string): Observable<ApiRelatedProductDetailList>;
  categoryProductStock(storeId: string, catalogId: string, categoryId: string, languageId: string,
                       withSubCategories: boolean): Observable<ApiProductsStockList>;
  productStock(storeId: string, catalogId: string, productIds: string, languageId: string): Observable<ApiProductsStockList>;
}

@Injectable()
export class ApiProductsService implements ApiProductsServiceInterface {

  constructor(
    private readonly http: HttpClient,
    private readonly requestService: ApiRequestService,
    private readonly ecommerceUrlResolverService: EcommerceUrlResolverService,
  ) {}

  public categoryProducts(storeId: string, catalogId: string, categoryId: string, languageId: string): Observable<ApiProductsList> {
    return this.requestService.requestUrl(`catalog/store/${storeId}/${catalogId}/category/${categoryId}/product`, languageId).pipe(
      switchMap((url: string) => this.http.get<ApiProductsList>(url))
    );
  }

  /**
   * Realiza una petición al endpoint /product de la versión del API especificada
   * @param storeId
   * @param catalogId
   * @param categoryId
   * @param languageId
   * @returns Observable con los datos devueltos por la petición /product
   */
  public categoryProductsV3(
    storeId: string,
    catalogId: string,
    categoryId: string,
    languageId: string
  ): Observable<ApiProductsV3> {
    const url = this
      .ecommerceUrlResolverService
      .resolveEcommerceAPI(3, EccomerceApiRoutes.CATEGORY_PRODUCTS, storeId, catalogId, categoryId, languageId);
    return this.http.get<ApiProductsV3>(url);
  }

  public categoryProductStock(
    storeId: string,
    catalogId: string,
    categoryId: string,
    languageId: string,
    withSubCategories: boolean = false
  ): Observable<ApiProductsStockList> {
    const slug = `catalog/store/${storeId}/${catalogId}/category/${categoryId}/stock`;
    return this.requestService.requestUrl(slug, languageId, [{ key: 'withSubCategories', value: withSubCategories ? 'true' : 'false' }]).pipe(
      switchMap((url: string) => this.http.get<ApiProductsStockList>(url))
    );
  }

  public searchProducts(storeId: string, catalogId: string, searchTerm: string, maxResults: string, languageId: string): Observable<ApiProductsList> {
    const customParams = [{ key: 'searchTerm', value: searchTerm }, { key: 'max', value: maxResults.toString() }];
    return this.requestService.requestUrl(`catalog/store/${storeId}/${catalogId}/product`, languageId, customParams).pipe(
      switchMap((url: string) => this.http.get<ApiProductsList>(url))
    );
  }

  public productDetail(storeId: string, catalogId: string, categoryId: string, productId: string, languageId: string): Observable<ApiProduct> {
    return this.requestService.requestUrl(
      `catalog/store/${storeId}/${catalogId}/category/${categoryId}/product/${productId}/detail`,
      languageId
    ).pipe(
      switchMap((url: string) => this.http.get<ApiProduct>(url))
    );
  }

  public productDetailByPartNumber(storeId: string, catalogId: string, partNumber: string, languageId: string): Observable<ApiProduct> {
    return this.requestService.requestUrl(`catalog/store/${storeId}/${catalogId}/product/${partNumber}`, languageId).pipe(
      switchMap((url: string) => this.http.get<ApiProduct>(url))
    );
  }

  public productMocacos(
    storeId: string,
    catalogId: string,
    categoryId: string,
    productId: string,
    languageId: string
  ): Observable<ApiProductMocacos> {
    return this.requestService.requestUrl(
      `catalog/store/${storeId}/${catalogId}/category/${categoryId}/product/${productId}/mocacos`,
      languageId
    ).pipe(
      switchMap((url: string) => this.http.get<ApiProductMocacos>(url))
    );
  }

  public productStock(storeId: string, catalogId: string, productIds: string, languageId: string): Observable<ApiProductsStockList> {
    const customParams: RequestParams = [{ key: 'productIds', value: productIds }];
    const slug = `catalog/store/${storeId}/${catalogId}/product/stock`;

    return this.requestService.requestUrl(slug, languageId, customParams).pipe(
      switchMap((url: string) => this.http.get<ApiProductsStockList>(url))
    );
  }

  public relatedProductsDetail(
    storeId: string,
    catalogId: string,
    categoryId: string,
    productId: string,
    languageId: string
  ): Observable<ApiRelatedProductDetailList> {
    const slug = `catalog/store/${storeId}/${catalogId}/category/${categoryId}/product/${productId}/relatedProducts`;
    return this.requestService.requestUrl(slug, languageId).pipe(
      switchMap((url: string) => this.http.get<ApiRelatedProductDetailList>(url))
    );
  }

  public sendToAFriendProduct(
    storeId: string,
    catalogId: string,
    categoryId: string,
    productId: string,
    languageId: string,
    body: ApiSendToAFriendProductRequestBody
  ): Observable<ApiEmptyResponse> {
    const slug = `catalog/store/${storeId}/${catalogId}/category/${categoryId}/product/${productId}/sendtoafriend`;

    return this.requestService.requestUrl(slug, languageId).pipe(
      switchMap((url: string) => this.http.post<ApiEmptyResponse>(url, body))
    );
  }

  public productsDetailArray(
    storeId: string,
    catalogId: string,
    productIds: string,
    languageId: string,
    key: ApiProductKeyType = 'productIds'
  ): Observable<ApiProductsList> {
    const url = this
      .ecommerceUrlResolverService
      .resolveEcommerceAPI(3, EccomerceApiRoutes.PRODUCT_ARRAY, storeId, catalogId, key, productIds, languageId);
    return this.http.get<ApiProductsList>(url).pipe(
      map((apiProductsList: ApiProductsList) => {
        // Remove not found products from list
        apiProductsList.products = apiProductsList.products.filter((product) => product.id);
        return apiProductsList;
      })
    );
  }

}
